import { useState } from "react";
import { History, Plus, Trash2, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { autoPeriods, normOverride, suggestShift, fmtClock } from "../lib/pickerSheet";
import { monthLabel } from "../lib/csvAnalysis";
import { slug } from "../lib/format";

const inp = "h-8 bg-[#0B0F19] border-slate-700 text-slate-100 font-mono text-xs [color-scheme:dark]";

const AddPeriod = ({ sid, onAdd }) => {
  const [since, setSince] = useState("");
  const [start, setStart] = useState("06:00");
  const [end, setEnd] = useState("14:00");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`shift-add-${sid}`}>
      <span className="label-mono">From</span>
      <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className={`${inp} w-[150px]`} data-testid={`shift-since-${sid}`} />
      <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className={`${inp} w-[100px]`} data-testid={`shift-start-${sid}`} />
      <span className="text-slate-500">–</span>
      <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={`${inp} w-[100px]`} data-testid={`shift-end-${sid}`} />
      <button data-testid={`shift-save-${sid}`} onClick={() => onAdd({ since: since || "0000-01-01", start, end })} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-ep hover:bg-ep-hover text-white"><Plus className="h-3.5 w-3.5" /> Add</button>
      <span className="text-[10px] text-slate-500">Leave date blank to apply from the start</span>
    </div>
  );
};

const HistoryDialog = ({ id, userDays, manual, open, onOpenChange, onSave }) => {
  const sid = slug(id);
  const auto = autoPeriods(userDays);
  const add = (p) => onSave([...manual.filter((m) => m.since !== p.since), p], `${id}: ${p.start}–${p.end} from ${p.since === "0000-01-01" ? "start" : p.since}`);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#111827] border-slate-800 text-slate-200" data-testid={`shift-history-${sid}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-slate-100">{id} · shift history</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Each day is measured against the assigned shift in force on that date; otherwise the shift auto-detected for that month.</DialogDescription>
        </DialogHeader>
        <p className="label-mono mt-2">Assigned (manual)</p>
        <div className="space-y-1.5" data-testid={`shift-manual-list-${sid}`}>
          {manual.length === 0 && <p className="text-xs text-slate-500">None — using auto-detected shifts.</p>}
          {manual.map((m) => (
            <div key={m.since} className="flex items-center gap-3 text-sm rounded-md border border-slate-800 px-3 py-1.5">
              <span className="font-mono text-xs text-slate-400 w-28">{m.since === "0000-01-01" ? "From start" : `From ${m.since}`}</span>
              <span className="font-mono text-slate-100">{m.start}–{m.end}</span>
              <button data-testid={`shift-remove-${sid}-${m.since}`} onClick={() => onSave(manual.filter((x) => x.since !== m.since), `${id}: entry removed`)} className="ml-auto p-1 rounded hover:bg-slate-800 text-slate-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <AddPeriod sid={sid} onAdd={add} />
        <p className="label-mono mt-4">Auto-detected by month</p>
        <div className="space-y-1" data-testid={`shift-auto-list-${sid}`}>
          {auto.length === 0 && <p className="text-xs text-slate-500">Not enough data.</p>}
          {auto.map((a) => (
            <div key={a.from} className="flex items-center gap-3 text-xs font-mono">
              <span className="text-slate-400 w-40">{monthLabel(a.from)}{a.to !== a.from ? ` – ${monthLabel(a.to)}` : ""}</span>
              <span className="text-slate-200">{a.start}–{a.end}</span>
              <span className="text-[10px] text-slate-500">{a.label}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ShiftCell = ({ id, userDays, override, onSave }) => {
  const [open, setOpen] = useState(false);
  const sid = slug(id);
  const manual = normOverride(override);
  const auto = autoPeriods(userDays);
  const lastAuto = auto[auto.length - 1];
  const sg = suggestShift(userDays);
  const latest = manual[manual.length - 1];
  const cur = latest ? { label: "Assigned", ...latest } : lastAuto;
  const changes = manual.length + Math.max(0, auto.length - 1);
  return (
    <div className="flex items-center gap-2 whitespace-nowrap" data-testid={`shift-cell-${sid}`}>
      {cur ? <span className="font-mono text-xs text-slate-200">{cur.start}–{cur.end}</span> : <span className="text-xs text-slate-500">Not enough data</span>}
      {cur && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${latest ? "border-ep/50 text-ep-soft" : cur.label === "Part-time" || cur.label === "Custom" ? "border-amber-500/40 text-amber-300" : "border-slate-700 text-slate-400"}`} data-testid={`shift-label-${sid}`}>{cur.label}{latest ? "" : " · auto"}</span>}
      {changes > 0 && <span className="text-[10px] text-slate-500" data-testid={`shift-changes-${sid}`}>{changes} change{changes === 1 ? "" : "s"}</span>}
      {sg && <span className="text-[10px] text-slate-500 font-mono" title="Median first / last pick">typ {fmtClock(sg.medFirst)}–{fmtClock(sg.medLast)}</span>}
      <button data-testid={`shift-edit-btn-${sid}`} onClick={() => setOpen(true)} className="p-1 rounded hover:bg-slate-800 text-slate-400" title="Shift history"><History className="h-3.5 w-3.5" /></button>
      {latest && <button data-testid={`shift-reset-${sid}`} onClick={() => onSave(null)} className="p-1 rounded hover:bg-slate-800 text-slate-400" title="Clear assigned shifts"><RotateCcw className="h-3.5 w-3.5" /></button>}
      <HistoryDialog id={id} userDays={userDays} manual={manual} open={open} onOpenChange={setOpen} onSave={onSave} />
    </div>
  );
};
